import { type Ref, ref, computed } from "vue";
import axios from "axios";

import Document from "@/models/Documents/Document";
import FileHelper from "@/helpers/utilities/FileHelper";

import { useSettingsStore } from "@/stores/settings";

interface QuickShareApiDocument {
  graphItemId: string;
  createdWhen: string;
  filename: string;
  [key: string]: unknown;
}

interface QuickshareComposableDependencies {
  downloadProgressState: Ref<Record<string, number | null>>;
}

export const useQuickshareComposable = (dependencies: QuickshareComposableDependencies) => {
  const quickShareDocumentsState: Ref<Document[] | null> = ref(null);

  const quickShareDocuments = computed(() => quickShareDocumentsState.value);

  const loadQuickShareDocuments = async () => {
    const settingsStore = useSettingsStore();
    const configForApiRequest = {
      headers: {
        apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
        "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY,
        sessiontoken: settingsStore.quickShareSessionToken
      }
    };
    const url = `${import.meta.env.VITE_APP_UCHI_API_ENDPOINT}/shares/${settingsStore.quickShareKey}/files`;
    const response = await axios.get(url, configForApiRequest);

    quickShareDocumentsState.value = (response.data as QuickShareApiDocument[]).map(quickShareDocument => {
      const document = (
        Document as unknown as { fromQuickShare(quickShareDocument: QuickShareApiDocument, baseUrl: string): Document }
      ).fromQuickShare(
        quickShareDocument,
        import.meta.env.VITE_APP_UCHI_API_ENDPOINT + "/shares/" + settingsStore.quickShareKey
      );
      document.itemId = quickShareDocument.graphItemId;
      (document as Document & { createdDateTime: unknown }).createdDateTime = quickShareDocument.createdWhen;
      document.extension = FileHelper.getFileExtensionObject(document.name);
      return document;
    });
  };

  const downloadQuickShareDocument = async (itemId: string) => {
    const settingsStore = useSettingsStore();
    const configForApiRequest = {
      headers: {
        apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
        "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY,
        sessiontoken: settingsStore.quickShareSessionToken,
        Accept: "application/octet-stream"
      },
      responseType: "blob" as const,
      onDownloadProgress: (progressEvent: { loaded: number; total: number }) => {
        dependencies.downloadProgressState.value[itemId] = Math.floor(
          (progressEvent.loaded / progressEvent.total) * 100
        );
      }
    };

    const progressBefore = JSON.parse(JSON.stringify(dependencies.downloadProgressState.value));
    progressBefore[itemId] = -1;
    dependencies.downloadProgressState.value = progressBefore;

    const blobResponse = await axios.get(
      `${import.meta.env.VITE_APP_UCHI_API_ENDPOINT}/shares/${settingsStore.quickShareKey}/files/${itemId}`,
      configForApiRequest
    );
    const progressAfter = dependencies.downloadProgressState.value;
    progressAfter[itemId] = null;
    dependencies.downloadProgressState.value = progressAfter;

    return blobResponse.data;
  };

  const downloadQuickShareDocumentsAsZip = async () => {
    const settingsStore = useSettingsStore();
    const configForApiRequest = {
      headers: {
        apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
        "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY,
        sessiontoken: settingsStore.quickShareSessionToken,
        Accept: "application/octet-stream"
      },
      responseType: "blob" as const,
      onDownloadProgress: (progressEvent: { loaded: number; total: number }) => {
        dependencies.downloadProgressState.value["Samlet Zip"] = Math.floor(
          (progressEvent.loaded / progressEvent.total) * 100
        );
      }
    };

    const progressBefore = JSON.parse(JSON.stringify(dependencies.downloadProgressState.value));
    progressBefore["Samlet Zip"] = -1;
    dependencies.downloadProgressState.value = progressBefore;

    const blobResponse = await axios.get(
      `${import.meta.env.VITE_APP_UCHI_API_ENDPOINT}/shares/${settingsStore.quickShareKey}/zip`,
      configForApiRequest
    );

    const progressAfter = dependencies.downloadProgressState.value;
    progressAfter["Samlet Zip"] = null;
    dependencies.downloadProgressState.value = progressAfter;
    return blobResponse.data;
  };

  return {
    quickShareDocumentsState,
    quickShareDocuments,
    loadQuickShareDocuments,
    downloadQuickShareDocument,
    downloadQuickShareDocumentsAsZip
  };
};
